/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { NotApplicableTypes, QuestionStatuses, TagStatus, UTRV_ASSURANCE_STATUSES, UTRV_STATUSES } from '@constants/status';
import { getNaType, isUtrvAssuranceComplete, isUtrvAssuranceCreated, isUtrvAssuranceRejected, isUtrvAssuranceRestated, isUtrvPartialAssurance } from '@utils/universalTrackerValue';
import { CurrentUserData } from '../../reducers/current-user';
import { Document } from 'flexsearch';
import { BlueprintContributions, ScopeQuestionGroupOptionalValue, ScopeQuestionOptionalValue, SurveySettings } from '@g17eco/types/survey';
import { MetricGroupDropdownItem, Tag } from '@g17eco/types/metricGroup';
import { Bookmark } from '@g17eco/types/bookmark';
import { isMatchSelectedPacks } from '@utils/metricGroup';

export interface DisableUtrsProps {
  surveyGroups: ScopeQuestionGroupOptionalValue[];
  blueprint?: BlueprintContributions;
  filterByStatus?: string[];
  filterByDelegationStatus?: string[];
  filterByGoal?: string[];
  filterByRegulatory?: string[];
  filterByModules?: string[];
  filterByTag?: string[];
  filterByBookmarks?: boolean;
  searchText?: string;
  searchIndex?: Document<unknown>;
  user?: CurrentUserData;
  tags?: Tag[];
  bookmarks?: Bookmark['targetId'][];
  metricGroups?: MetricGroupDropdownItem[];
}

const isUtrvStatus = (filteredStatus: string, question: ScopeQuestionOptionalValue) => {
  if (!question.utrv) {
    return false;
  }
  const aggregatedStatus = question.utrv.status;
  switch (filteredStatus) {
    case QuestionStatuses.NA:
      return getNaType(question.utrv) === NotApplicableTypes.na;
    case QuestionStatuses.NR:
      return getNaType(question.utrv) === NotApplicableTypes.nr;
    case QuestionStatuses.Created:
    case QuestionStatuses.Updated:
    case QuestionStatuses.Rejected:
    case QuestionStatuses.Verified:
      return filteredStatus === aggregatedStatus;
    case QuestionStatuses.Public:
      return !question.utrv.isPrivate;
    case QuestionStatuses.Private:
      return question.utrv.isPrivate === true;
    default:
      return false;
  }
};

const isUtrvAssuranceStatus = (filteredStatus: string, question: ScopeQuestionOptionalValue) => {
  if (!question.utrv) {
    return false;
  }
  switch (filteredStatus) {
    case QuestionStatuses.SentToAssurer:
      return !!question.utrv.assuranceStatus;
    case QuestionStatuses.NotSelectedForAssurance:
      return !question.utrv.assuranceStatus;
    case QuestionStatuses.Assured:
      return isUtrvAssuranceComplete(question.utrv.assuranceStatus);
    case QuestionStatuses.PartiallyAssured:
      return isUtrvPartialAssurance(question.utrv.assuranceStatus);
    case QuestionStatuses.NotAssured:
      return isUtrvAssuranceCreated(question.utrv.assuranceStatus);
    case QuestionStatuses.Disputed:
      return isUtrvAssuranceRejected(question.utrv.assuranceStatus);
    case QuestionStatuses.Restated:
      return isUtrvAssuranceRestated(question.utrv.assuranceStatus);
    default:
      return false;
  }
};

export const disableByGoal = ({ surveyGroups, filterByGoal, blueprint }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByGoal' | 'blueprint'>) => {

  if (!filterByGoal || filterByGoal.length === 0) {
    return [];
  }

  const goalCodes = filterByGoal.map((goal: string) => `sdg/${goal}`);

  const disabledUTRs: string[] = [];

  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      const utrCode = question.universalTracker.getCode();
      if (goalCodes.every((code) => !blueprint?.[utrCode]?.includes(code))) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    })
  );
  return disabledUTRs;
}

export const disableByStatus = ({ surveyGroups, filterByStatus }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByStatus'>) => {

  if (!filterByStatus || filterByStatus.length === 0) {
    return [];
  }

  const utrvStatuses = filterByStatus.filter((s) => UTRV_STATUSES.includes(s as QuestionStatuses));
  const utrvAssuranceStatuses = filterByStatus.filter((s) => UTRV_ASSURANCE_STATUSES.includes(s as QuestionStatuses));

  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      const matchUtrvStatuses = utrvStatuses.length ? utrvStatuses.some((status) => isUtrvStatus(status, question)) : true;
      const matchUtrvAssuranceStatuses = utrvAssuranceStatuses.length
        ? utrvAssuranceStatuses.some((status) => isUtrvAssuranceStatus(status, question))
        : true;
      if (!(matchUtrvStatuses && matchUtrvAssuranceStatuses)) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    })
  );
  return disabledUTRs;
}

export const disableByRegulatory = ({ surveyGroups, filterByRegulatory }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByRegulatory'>) => {

  if (!filterByRegulatory || filterByRegulatory.length === 0) {
    return [];
  }

  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      filterByRegulatory.forEach((code) => {
        if (!question.universalTracker.hasAlternativeInfo(code) && !question.universalTracker.hasSubTag(code)) {
          disabledUTRs.push(question.universalTracker.getId());
        }
      });
    })
  );
  return disabledUTRs;
}

export const disableByModules = ({
  surveyGroups,
  filterByModules,
  metricGroups = [],
}: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByModules' | 'metricGroups'>) => {
  if (!filterByModules || filterByModules.length === 0) {
    return [];
  }

  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      if (!isMatchSelectedPacks({utr: question.universalTracker.getRaw(), packs: filterByModules, metricGroups })) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    })
  );
  return disabledUTRs;
};

export const disableByDelegationStatus = ({ surveyGroups, filterByDelegationStatus, user }: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByDelegationStatus' | 'user'>) => {

  if (!filterByDelegationStatus || filterByDelegationStatus.length === 0) {
    return [];
  }
  const currentUserId = user?._id;

  const getDelegationStatusByCode = (question: ScopeQuestionOptionalValue, code: string) => {
    switch (code) {
      case 'user-a':
        return currentUserId && question.utrv?.stakeholders && (
          question.utrv.stakeholders.stakeholder.includes(currentUserId) || question.utrv.stakeholders.verifier.includes(currentUserId)
        );
      case 'con-u':
        return !question.utrv?.stakeholders || question.utrv.stakeholders.stakeholder.length === 0;
      case 'con-a':
        return question.utrv?.stakeholders && question.utrv.stakeholders.stakeholder.length > 0;
      case 'ver-u':
        return !question.utrv?.stakeholders || question.utrv.stakeholders.verifier.length === 0;
      case 'ver-a':
        return question.utrv?.stakeholders && question.utrv.stakeholders.verifier.length > 0;
      default:
        // Falls back to checking if the code is actually a user._id
        return question.utrv && (
          question.utrv.stakeholders.stakeholder.includes(code) || question.utrv.stakeholders.verifier.includes(code)
        );
    }
  }
  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      filterByDelegationStatus.forEach((filterDelegationStatus: string) => {
        const disabled = !getDelegationStatusByCode(question, filterDelegationStatus);
        if (disabled) {
          disabledUTRs.push(question.universalTracker.getId());
        }
      });
    })
  );
  return disabledUTRs;
}

export const getFilteredUtrIds = (filterByTag: string[], tags: Tag[]) => {
  const tagMap = new Map<Tag['_id'], Tag['universalTrackers']>();
  tags.forEach((tag) => tagMap.set(tag._id, tag.universalTrackers));

  return filterByTag.reduce((acc, curTagId, index) => {
    const utrIds = tagMap.get(curTagId) || [];
    if (index === 0) {
      return utrIds;
    }
    return acc.filter((utrId) => utrIds.includes(utrId));
  }, [] as string[]);
};

export const getTaggedUtrIds = (tags: Tag[]) => {
  const tagSet = new Set<string>();
  tags.forEach(tag => {
    tag.universalTrackers?.forEach(utrId => {
      tagSet.add(utrId);
    });
  });

  return Array.from(tagSet);
}

export const disableByTag = ({
  surveyGroups,
  filterByTag,
  tags,
}: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByTag' | 'tags'>) => {
  if (!filterByTag || filterByTag.length === 0 || !tags) {
    return [];
  }
  const hasBothNoTagAndOthers = filterByTag.includes(TagStatus.NoTag) && filterByTag.length > 1;
  const hasNoTagOnly = filterByTag.includes(TagStatus.NoTag) && filterByTag.length === 1;
  const hasOtherTagsOnly = !filterByTag.includes(TagStatus.NoTag);

  const filteredUtrIds = getFilteredUtrIds(filterByTag, tags);
  const taggedUtrIds = getTaggedUtrIds(tags);

  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) =>
    questionGroup.list.forEach((question) => {
      const noTagFilterOnly = hasNoTagOnly && taggedUtrIds.includes(question.universalTracker.getId());
      const otherTagsFilterOnly = hasOtherTagsOnly && !filteredUtrIds.includes(question.universalTracker.getId());

      if (hasBothNoTagAndOthers || noTagFilterOnly || otherTagsFilterOnly) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    })
  );
  return disabledUTRs;
};

export const disableBySearch = ({ surveyGroups, searchText, searchIndex }: Pick<DisableUtrsProps, 'surveyGroups' | 'searchText' | 'searchIndex'>) => {

  if (!searchText || !searchIndex) {
    return [];
  }

  const result = searchIndex.search(searchText);
  const matchedIds = new Set(result.map((question) => question.result).flat())


  const disabledUTRs: string[] = [];
  surveyGroups.forEach((questionGroup) => {
    questionGroup.list.forEach((question) => {
      if (!matchedIds.has(question.universalTracker.getId())) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    })
  });

  return disabledUTRs;
}

export const disableByBookmarks = ({
  surveyGroups,
  filterByBookmarks,
  bookmarks,
}: Pick<DisableUtrsProps, 'surveyGroups' | 'filterByBookmarks' | 'bookmarks'>) => {
  if (!filterByBookmarks || !bookmarks?.length) {
    return [];
  }

  const disabledUTRs: string[] = [];

  surveyGroups.forEach((questionGroup) => {
    questionGroup.list.forEach((question) => {
      if (question.utrv?._id && !bookmarks.includes(question.utrv._id)) {
        disabledUTRs.push(question.universalTracker.getId());
      }
    });
  });

  return disabledUTRs;
};


export type ExtendedFilters =  Partial<Pick<SurveySettings,
  | 'filterByStatus'
  | 'filterByDelegationStatus'
  | 'filterByGoal'
  | 'filterByModules'
  | 'filterByTag'
>>

/**
 * These filters are only shown in the extended filter
 */
export const hasExtendedFilter = (props: ExtendedFilters) => {
  const {
    filterByStatus,
    filterByDelegationStatus,
    filterByModules,
    filterByGoal,
    filterByTag,
  } = props;

  if (filterByGoal && filterByGoal.length > 0) {
    return true;
  }
  if (filterByStatus && filterByStatus.length > 0) {
    return true;
  }
  if (filterByModules && filterByModules.length > 0) {
    return true;
  }
  if (filterByDelegationStatus && filterByDelegationStatus.length > 0) {
    return true;
  }
  if (filterByTag && filterByTag.length > 0) {
    return true;
  }

  return false;
}

export const hasUTRVFilter = (props: Partial<SurveySettings>) => {
  const { filterByRegulatory, searchText, filterByBookmarks } = props;

  if (hasExtendedFilter(props)) {
    return true;
  }

  if (filterByRegulatory && filterByRegulatory.length > 0) {
    return true;
  }
  if (searchText && searchText.length > 0) {
    return true;
  }
  if (filterByBookmarks) {
    return true;
  }
  return false;
};

export const generateDisabledUtrs = (props: DisableUtrsProps) => [
  ...disableByGoal(props),
  ...disableByStatus(props),
  ...disableByRegulatory(props),
  ...disableByModules(props),
  ...disableByDelegationStatus(props),
  ...disableByTag(props),
  ...disableBySearch(props),
  ...disableByBookmarks(props),
];
